import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Users, AlertTriangle, Check, X, Pause, Play,
  Wrench, Truck, Phone, Mail, DollarSign, Eye, Loader2,
  Shield, Search, Filter, MoreVertical
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skills options
const MECHANIC_SKILLS = [
  'Engine Diagnostics', 'Brake Systems', 'Electrical Systems', 'Oil Change & Maintenance',
  'Transmission Repair', 'AC/Heating Repair', 'Suspension & Steering', 'Exhaust Systems'
];

const ROADSIDE_SKILLS = [
  'Towing (Flatbed)', 'Towing (Wheel Lift)', 'Jump Start Services', 'Tire Change',
  'Lockout Services', 'Fuel Delivery', 'Battery Services', 'Heavy Duty Towing'
];

/**
 * CreateProviderDialog - Modal to create new provider accounts
 */
function CreateProviderDialog({ open, onClose, onCreated }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    user_type: 'mechanic',
    skills: []
  });

  const skillOptions = formData.user_type === 'mechanic' ? MECHANIC_SKILLS : ROADSIDE_SKILLS;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/providers/create`, formData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success('Provider account created successfully!');
      onCreated(response.data.user);
      onClose();
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        user_type: 'mechanic',
        skills: []
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-blue-600" />
            Create Provider Account
          </DialogTitle>
          <DialogDescription>
            Create a new mechanic or roadside technician account. The account will be auto-approved.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Account Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, user_type: 'mechanic', skills: [] }))}
                className={`p-4 border rounded-lg flex flex-col items-center gap-2 transition-all ${
                  formData.user_type === 'mechanic'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Wrench className="w-6 h-6" />
                <span className="font-medium">Mechanic</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, user_type: 'roadside_tech', skills: [] }))}
                className={`p-4 border rounded-lg flex flex-col items-center gap-2 transition-all ${
                  formData.user_type === 'roadside_tech'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Truck className="w-6 h-6" />
                <span className="font-medium">Roadside Tech</span>
              </button>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="555-123-4567"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="provider@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Password *</label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Minimum 8 characters"
              minLength={8}
              required
            />
          </div>

          {/* Skills */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Skills ({formData.skills.length} selected)
            </label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg bg-gray-50">
              {skillOptions.map(skill => (
                <button
                  key={skill}
                  type="button"
                  onClick={() => toggleSkill(skill)}
                  className={`px-3 py-1 rounded-full text-sm transition-all ${
                    formData.skills.includes(skill)
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border text-gray-600 hover:border-blue-400'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Account
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SuspendDialog - Modal to suspend/unsuspend provider
 */
function SuspendDialog({ provider, open, onClose, onUpdated }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');

  const isSuspended = provider?.suspension_status === 'suspended' || provider?.suspension_status === 'pending_suspension';

  const handleAction = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/providers/${provider.user_id}/suspend`, {
        action: isSuspended ? 'unsuspend' : 'suspend',
        reason
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success(isSuspended ? 'Provider unsuspended' : 'Provider suspended');
      onUpdated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isSuspended ? (
              <>
                <Play className="w-5 h-5 text-green-600" />
                Unsuspend Provider
              </>
            ) : (
              <>
                <Pause className="w-5 h-5 text-red-600" />
                Suspend Provider
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSuspended
              ? `Are you sure you want to unsuspend ${provider.name}?`
              : `This will suspend ${provider.name}'s account. They won't be able to accept new jobs.`}
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            {provider.user_type === 'mechanic' ? 
              <Wrench className="w-8 h-8 text-green-600" /> :
              <Truck className="w-8 h-8 text-orange-600" />
            }
            <div>
              <p className="font-semibold">{provider.name}</p>
              <p className="text-sm text-gray-500">{provider.email}</p>
            </div>
          </div>
        </div>

        {!isSuspended && (
          <div>
            <label className="block text-sm font-medium mb-1">Reason for Suspension</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason (optional)"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be shown to the provider when they try to access their account.
            </p>
          </div>
        )}

        {!isSuspended && provider.is_online && (
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-700">
              This provider is currently online. If they have an active job, suspension will take effect after the job completes.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleAction}
            disabled={loading}
            className={isSuspended ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {isSuspended ? 'Unsuspend' : 'Suspend'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ProviderManagement - Main admin component for managing providers
 */
export default function ProviderManagement() {
  const { getAuthHeaders } = useAuth();
  const [providers, setProviders] = useState([]);
  const [suspendedProviders, setSuspendedProviders] = useState([]);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);

  const fetchProviders = useCallback(async () => {
    try {
      const [liveRes, suspendedRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/providers/live-locations`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/providers/suspended`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/pending-providers`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      
      setProviders(liveRes.data.providers || []);
      setSuspendedProviders(suspendedRes.data.providers || []);
      setPendingProviders(pendingRes.data || []);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProviders();
    const interval = setInterval(fetchProviders, 30000);
    return () => clearInterval(interval);
  }, [fetchProviders]);

  const approveProvider = async (userId) => {
    try {
      await axios.put(`${API}/admin/providers/${userId}/approve`, { action: 'approve' }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Provider approved');
      fetchProviders();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const rejectProvider = async (userId) => {
    try {
      await axios.put(`${API}/admin/providers/${userId}/approve`, { action: 'reject', reason: 'Application rejected' }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.info('Provider rejected');
      fetchProviders();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  // Filter providers
  const filteredProviders = providers.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || p.user_type === filterType;
    return matchesSearch && matchesType;
  });

  const stats = {
    total: providers.length,
    online: providers.filter(p => p.is_online).length,
    suspended: suspendedProviders.length,
    pending: pendingProviders.length
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="provider-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Provider Management</h2>
          <p className="text-sm sm:text-base text-gray-500">Create, manage, and monitor provider accounts</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto h-12 sm:h-10">
          <Plus className="w-4 h-4 mr-2" />
          Create Provider
        </Button>
      </div>

      {/* Stats - Mobile optimized */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold truncate">{stats.total}</p>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold text-green-600 truncate">{stats.online}</p>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Online</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Pause className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold text-red-600 truncate">{stats.suspended}</p>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Suspended</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold text-yellow-600 truncate">{stats.pending}</p>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="all">All Providers</TabsTrigger>
          <TabsTrigger value="pending">
            Pending Approval
            {pendingProviders.length > 0 && (
              <Badge className="ml-2 bg-yellow-500">{pendingProviders.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="suspended">
            Suspended
            {suspendedProviders.length > 0 && (
              <Badge className="ml-2 bg-red-500">{suspendedProviders.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* All Providers Tab */}
        <TabsContent value="all" className="mt-4">
          {/* Search & Filter */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search providers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Types</option>
              <option value="mechanic">Mechanics</option>
              <option value="roadside_tech">Roadside Techs</option>
            </select>
          </div>

          {/* Provider List */}
          <div className="space-y-3">
            {filteredProviders.map(provider => (
              <ProviderCard
                key={provider.user_id}
                provider={provider}
                onSuspend={() => {
                  setSelectedProvider(provider);
                  setShowSuspendDialog(true);
                }}
              />
            ))}
            {filteredProviders.length === 0 && (
              <p className="text-center py-8 text-gray-500">No providers found</p>
            )}
          </div>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending" className="mt-4">
          <div className="space-y-3">
            {pendingProviders.map(provider => (
              <Card key={provider.user_id} className="border-yellow-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {provider.user_type === 'mechanic' ?
                        <Wrench className="w-8 h-8 text-green-600" /> :
                        <Truck className="w-8 h-8 text-orange-600" />
                      }
                      <div>
                        <p className="font-semibold">{provider.name}</p>
                        <p className="text-sm text-gray-500">{provider.email}</p>
                        <p className="text-xs text-gray-400">
                          Applied: {new Date(provider.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => rejectProvider(provider.user_id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => approveProvider(provider.user_id)}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {pendingProviders.length === 0 && (
              <p className="text-center py-8 text-gray-500">No pending approvals</p>
            )}
          </div>
        </TabsContent>

        {/* Suspended Tab */}
        <TabsContent value="suspended" className="mt-4">
          <div className="space-y-3">
            {suspendedProviders.map(provider => (
              <Card key={provider.user_id} className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {provider.user_type === 'mechanic' ?
                        <Wrench className="w-8 h-8 text-red-600" /> :
                        <Truck className="w-8 h-8 text-red-600" />
                      }
                      <div>
                        <p className="font-semibold">{provider.name}</p>
                        <p className="text-sm text-gray-500">{provider.email}</p>
                        <p className="text-xs text-red-600">
                          Reason: {provider.suspension_reason || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setSelectedProvider(provider);
                        setShowSuspendDialog(true);
                      }}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Unsuspend
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {suspendedProviders.length === 0 && (
              <p className="text-center py-8 text-gray-500">No suspended providers</p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CreateProviderDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={() => fetchProviders()}
      />

      <SuspendDialog
        provider={selectedProvider}
        open={showSuspendDialog}
        onClose={() => {
          setShowSuspendDialog(false);
          setSelectedProvider(null);
        }}
        onUpdated={fetchProviders}
      />
    </div>
  );
}

/**
 * ProviderCard - Individual provider card with actions
 */
function ProviderCard({ provider, onSuspend }) {
  const Icon = provider.user_type === 'mechanic' ? Wrench : Truck;
  const isSuspended = provider.suspension_status === 'suspended';
  const isPending = provider.suspension_status === 'pending_suspension';

  return (
    <Card className={`${isSuspended ? 'border-red-200 bg-red-50' : isPending ? 'border-yellow-200 bg-yellow-50' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isSuspended 
                ? 'bg-red-100' 
                : provider.is_online 
                  ? 'bg-green-100' 
                  : 'bg-gray-100'
            }`}>
              <Icon className={`w-6 h-6 ${
                isSuspended 
                  ? 'text-red-600' 
                  : provider.is_online 
                    ? 'text-green-600' 
                    : 'text-gray-500'
              }`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold">{provider.name}</p>
                {provider.is_online && !isSuspended && (
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                )}
                {isSuspended && <Badge variant="destructive">Suspended</Badge>}
                {isPending && <Badge className="bg-yellow-500">Pending Suspension</Badge>}
              </div>
              <p className="text-sm text-gray-500">{provider.email}</p>
              {provider.phone && (
                <p className="text-xs text-gray-400">{provider.phone}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {provider.active_job && (
              <Badge variant="outline" className="bg-blue-50 text-blue-600">
                On Job
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => window.location.href = `tel:${provider.phone}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.location.href = `mailto:${provider.email}`}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onSuspend}>
                  {isSuspended ? (
                    <>
                      <Play className="w-4 h-4 mr-2 text-green-600" />
                      <span className="text-green-600">Unsuspend</span>
                    </>
                  ) : (
                    <>
                      <Pause className="w-4 h-4 mr-2 text-red-600" />
                      <span className="text-red-600">Suspend</span>
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
